import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftIcon, KeyRoundIcon, Loader2Icon, LogInIcon, MailIcon } from "lucide-react"
import { useEffect, useId, useState, type ReactNode } from "react"
import { toast } from "sonner"
import {
  AuthCard,
  BeaconMark,
  Callout,
  GoogleMark,
  SlackMark,
  useUnsavedWork,
  useViewport,
} from "@/design-system/index.ts"
import { Button } from "@/primitives/button.tsx"
import { Input } from "@/primitives/input.tsx"
import { cn } from "@/primitives/utils"
import {
  emailMeACode,
  emailMeAResetCode,
  meQuery,
  resetPassword,
  signInProvidersQuery,
  signInToConnectAgent,
  signInWith,
  signInWithCode,
  signInWithPassword,
  type SignInProvider,
  type SocialProvider,
} from "./client.ts"
import {
  codeTyped,
  describePasswordRefusal,
  emailCodeFirst,
  howToSignIn,
  isAddress,
  isSignInName,
  waysShown,
} from "./describe.ts"
import { destinationOf } from "./destination.ts"

/*
 * What anyone who isn't signed in sees, at whatever address they opened: one card on the canvas,
 * with no shell around it, since there is nowhere to go until Beacon knows who they are. Nothing
 * about accounts shows here; Beacon's record decides who may sign in, whichever way they come.
 */

/**
 * The sign-in card. `next` is the path, search and hash the visitor asked for, which Google sends
 * them back to once they're signed in; a deep link (a ticket shared in Slack) says where it leads.
 * With `connectingAgent`, the signed query an agent's sign-in arrived with, signing in carries on
 * to that agent's consent instead. Where the stage sends email, "Email me a code" turns the card
 * into asking for an address and then for the code sent to it. "Sign in with a password" asks for a
 * username or email and a password, and offers a code to set a new one when it's forgotten. In the
 * installed app on an iPhone or an iPad, "Email me a code" comes first (`emailCodeFirst`). While
 * the browser leaves for Google or Slack the page holds unsaved work, so an update doesn't reload
 * it from under the sign-in.
 */
export function SignInScreen({
  next,
  connectingAgent,
}: {
  next: string
  connectingAgent?: string
}) {
  const [redirecting, setRedirecting] = useState<SocialProvider>()
  // Which way in the card is showing, past its buttons, and whom a password reset is for.
  const [way, setWay] = useState<"code" | "password" | "reset">()
  const [forgotten, setForgotten] = useState("")
  // Google until the stage says otherwise, so the card never waits on the answer.
  const { data: offered = ["google"] } = useQuery(signInProvidersQuery)
  const destination = connectingAgent === undefined ? destinationOf(next) : undefined

  useEffect(() => {
    document.title = "Sign in · Beacon"
  }, [])

  // In the installed app on an iPhone or an iPad, a code by email comes first.
  const { standalone } = useViewport()
  const codeFirst = emailCodeFirst(standalone, navigator)
  // Leaving for Google or Slack must not be interrupted by an update reloading the page.
  useUnsavedWork(redirecting !== undefined)

  // The browser leaves for Google or Slack once the sign-in starts, so the spinner stays until it
  // does. A start that fails — the web API unreachable — hands the buttons back.
  async function continueWith(provider: SocialProvider) {
    setRedirecting(provider)
    const failed = () => {
      setRedirecting(undefined)
      toast.error(`Beacon couldn't reach ${providerNames[provider]}. Try again.`)
    }
    try {
      const { error } =
        connectingAgent === undefined
          ? await signInWith(provider, next)
          : await signInToConnectAgent(provider, connectingAgent)
      if (error) failed()
    } catch {
      failed()
    }
  }

  // Each way in, as its button names, marks and takes it.
  const buttons: Record<SignInProvider, { label: string; Mark: ReactNode; choose: () => void }> = {
    google: {
      label: "Continue with Google",
      Mark: <GoogleMark size={16} />,
      choose: () => void continueWith("google"),
    },
    slack: {
      label: "Continue with Slack",
      Mark: <SlackMark size={16} />,
      choose: () => void continueWith("slack"),
    },
    "email-code": { label: "Email me a code", Mark: <MailIcon />, choose: () => setWay("code") },
    password: {
      label: "Sign in with a password",
      Mark: <KeyRoundIcon />,
      choose: () => setWay("password"),
    },
  }

  return (
    <AuthCard>
      <div className="text-center">
        <div className="flex justify-center">
          <BeaconMark size={40} />
        </div>
        <h1 className="mt-5 text-heading font-semibold">
          {connectingAgent === undefined ? "Sign in to Beacon" : "Sign in to connect an agent"}
        </h1>
        <p className="mt-1.5 text-ui text-muted-foreground">
          {connectingAgent === undefined
            ? "The company's Slack assistant, with its knowledge, projects and settings."
            : "Your agent will work in Beacon as you, once you've signed in and allowed it."}
        </p>
        {destination && (
          <div className="mt-6 text-left">
            <Callout tone="neutral" icon={LogInIcon}>
              Sign in to open {destination}
            </Callout>
          </div>
        )}
        {way === "code" ? (
          <EmailCode connectingAgent={connectingAgent} onBack={() => setWay(undefined)} />
        ) : way === "password" ? (
          <PasswordSignIn
            connectingAgent={connectingAgent}
            onForgot={(typed) => {
              setForgotten(typed)
              setWay("reset")
            }}
            onBack={() => setWay(undefined)}
          />
        ) : way === "reset" ? (
          <PasswordReset
            typed={forgotten}
            connectingAgent={connectingAgent}
            onBack={() => setWay("password")}
          />
        ) : (
          <>
            <div className="mt-6 flex flex-col gap-2">
              {waysShown(offered, codeFirst).map(({ way: shown, look }) => {
                const { label, Mark, choose } = buttons[shown]
                return (
                  <Button
                    key={shown}
                    variant={look === "lead" ? "outline" : "ghost"}
                    size="lg"
                    className={cn("w-full", look === "quiet" && "text-muted-foreground")}
                    disabled={redirecting !== undefined}
                    onClick={choose}
                  >
                    {redirecting === shown ? <Loader2Icon className="animate-spin" /> : Mark}
                    {label}
                  </Button>
                )
              })}
            </div>
            <p className="mt-4 text-xs text-subtle-foreground">{howToSignIn(offered)}</p>
          </>
        )}
      </div>
    </AuthCard>
  )
}

/** Each way to sign in through someone else, as the card names it. */
const providerNames = { google: "Google", slack: "Slack" } as const satisfies Record<
  SocialProvider,
  string
>

/**
 * Signing in with an emailed code: an address, then the code sent to it. Beacon answers the same
 * whoever the address belongs to, so the card says a code is on its way only if the address is one
 * of the visitor's in Beacon. Signed in, the shell takes over at the address they opened, or the
 * agent's request carries on.
 */
function EmailCode({
  connectingAgent,
  onBack,
}: {
  connectingAgent: string | undefined
  onBack: () => void
}) {
  const id = useId()
  const queryClient = useQueryClient()
  const [address, setAddress] = useState("")
  const [sentTo, setSentTo] = useState<string>()
  const [typed, setTyped] = useState("")
  const [pending, setPending] = useState(false)
  const [refused, setRefused] = useState(false)
  const code = codeTyped(typed)

  async function send() {
    if (!isAddress(address)) return
    setPending(true)
    try {
      const { error } = await emailMeACode(address)
      if (error) toast.error("Beacon couldn't send a code just now. Try again.")
      else setSentTo(address.trim())
    } catch {
      toast.error("Beacon couldn't send a code just now. Try again.")
    } finally {
      setPending(false)
    }
  }

  async function signIn() {
    if (!sentTo || !code) return
    setPending(true)
    setRefused(false)
    try {
      const { data, error } = await signInWithCode(sentTo, code, connectingAgent)
      if (error) {
        setRefused(true)
        return
      }
      await carryOn(data, queryClient)
    } catch {
      toast.error("Beacon couldn't be reached. Try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <form
      className="mt-6 space-y-3 text-left"
      onSubmit={(event) => {
        event.preventDefault()
        void (sentTo ? signIn() : send())
      }}
    >
      {sentTo === undefined ? (
        <>
          <label htmlFor={`${id}-address`} className="block text-ui font-medium">
            Your email
          </label>
          <Input
            id={`${id}-address`}
            type="email"
            enterKeyHint="send"
            autoComplete="email"
            autoFocus
            value={address}
            placeholder="you@gsmsystems.com"
            onChange={(event) => setAddress(event.target.value)}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={pending || !isAddress(address)}
          >
            {pending && <Loader2Icon className="animate-spin" />}
            Email me a code
          </Button>
        </>
      ) : (
        <>
          <p className="text-ui text-muted-foreground">
            If <span className="font-medium text-foreground">{sentTo}</span> is one of your
            addresses in Beacon, a code is on its way. It works for 5 minutes.
          </p>
          <label htmlFor={`${id}-code`} className="block text-ui font-medium">
            Code
          </label>
          <Input
            id={`${id}-code`}
            inputMode="numeric"
            enterKeyHint="go"
            autoComplete="one-time-code"
            autoFocus
            value={typed}
            aria-invalid={refused || undefined}
            onChange={(event) => {
              setTyped(event.target.value)
              setRefused(false)
            }}
          />
          {refused && (
            <p className="text-xs text-destructive">
              That code didn't work. Check it, or ask for a new one.
            </p>
          )}
          <Button type="submit" size="lg" className="w-full" disabled={pending || !code}>
            {pending && <Loader2Icon className="animate-spin" />}
            Sign in
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={pending}
            onClick={() => void send()}
          >
            Send another code
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        disabled={pending}
        onClick={onBack}
      >
        <ArrowLeftIcon />
        Other ways to sign in
      </Button>
    </form>
  )
}

/**
 * Where a signed-in browser goes next: on with an agent's request when Better Auth names a `url`,
 * or to the address the visitor opened, which the shell takes over once it knows who they are.
 */
async function carryOn(data: unknown, queryClient: ReturnType<typeof useQueryClient>) {
  const next = (data as { url?: unknown } | null)?.url
  if (typeof next === "string") window.location.assign(next)
  else await queryClient.invalidateQueries({ queryKey: meQuery.queryKey })
}

/**
 * Signing in with a password and a username or any of the visitor's verified emails. A wrong
 * password, a locked account and someone Beacon doesn't know read alike, so the card says what a
 * lock is without saying whether one applies.
 */
function PasswordSignIn({
  connectingAgent,
  onForgot,
  onBack,
}: {
  connectingAgent: string | undefined
  onForgot: (typed: string) => void
  onBack: () => void
}) {
  const id = useId()
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState("")
  const [password, setPassword] = useState("")
  const [pending, setPending] = useState(false)
  const [refused, setRefused] = useState<"wrong" | "too-many">()

  async function signIn() {
    if (!isSignInName(typed) || !password) return
    setPending(true)
    setRefused(undefined)
    try {
      const { data, error } = await signInWithPassword(typed, password, connectingAgent)
      if (error) {
        setRefused(error.status === 429 ? "too-many" : "wrong")
        return
      }
      await carryOn(data, queryClient)
    } catch {
      toast.error("Beacon couldn't be reached. Try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <form
      className="mt-6 space-y-3 text-left"
      onSubmit={(event) => {
        event.preventDefault()
        void signIn()
      }}
    >
      <label htmlFor={`${id}-name`} className="block text-ui font-medium">
        Username or email
      </label>
      <Input
        id={`${id}-name`}
        autoComplete="username"
        enterKeyHint="go"
        autoFocus
        value={typed}
        onChange={(event) => {
          setTyped(event.target.value)
          setRefused(undefined)
        }}
      />
      <label htmlFor={`${id}-password`} className="block text-ui font-medium">
        Password
      </label>
      <Input
        id={`${id}-password`}
        type="password"
        autoComplete="current-password"
        enterKeyHint="go"
        value={password}
        aria-invalid={refused !== undefined || undefined}
        onChange={(event) => {
          setPassword(event.target.value)
          setRefused(undefined)
        }}
      />
      {refused && (
        <p className="text-xs text-destructive">
          {refused === "too-many"
            ? "Too many tries from here. Wait a few seconds, then try again."
            : "That username or password isn't right. After 5 wrong tries, signing in with a password pauses for 15 minutes."}
        </p>
      )}
      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={pending || !isSignInName(typed) || !password}
      >
        {pending && <Loader2Icon className="animate-spin" />}
        Sign in
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        disabled={pending}
        onClick={() => onForgot(typed.trim())}
      >
        Forgot your password?
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        disabled={pending}
        onClick={onBack}
      >
        <ArrowLeftIcon />
        Other ways to sign in
      </Button>
    </form>
  )
}

/**
 * Setting a new password with a code: a username or email, then the code Beacon emails (to the
 * address typed, or a username's primary email) and the new password, which then signs them in.
 * Beacon answers the same whoever was typed, so the card says a code is on its way only if they
 * are someone in Beacon.
 */
function PasswordReset({
  typed: first,
  connectingAgent,
  onBack,
}: {
  typed: string
  connectingAgent: string | undefined
  onBack: () => void
}) {
  const id = useId()
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState(first)
  const [sentFor, setSentFor] = useState<string>()
  const [code, setCode] = useState("")
  const [password, setPassword] = useState("")
  const [pending, setPending] = useState(false)
  const [refused, setRefused] = useState<string>()
  const otp = codeTyped(code)

  async function send() {
    if (!isSignInName(typed)) return
    setPending(true)
    try {
      const { error } = await emailMeAResetCode(typed)
      if (error) toast.error("Beacon couldn't send a code just now. Try again.")
      else setSentFor(typed.trim())
    } catch {
      toast.error("Beacon couldn't send a code just now. Try again.")
    } finally {
      setPending(false)
    }
  }

  async function reset() {
    if (!sentFor || !otp || !password) return
    setPending(true)
    setRefused(undefined)
    try {
      const { error } = await resetPassword(sentFor, otp, password)
      if (error) {
        setRefused(
          error.code === "INVALID_OTP" ||
            error.code === "OTP_EXPIRED" ||
            error.code === "TOO_MANY_ATTEMPTS"
            ? "That code didn't work. Check it, or ask for a new one."
            : describePasswordRefusal(error.code),
        )
        return
      }
      const signedIn = await signInWithPassword(sentFor, password, connectingAgent)
      if (signedIn.error) {
        toast.success("Your new password is set. Sign in with it.")
        onBack()
        return
      }
      await carryOn(signedIn.data, queryClient)
    } catch {
      toast.error("Beacon couldn't be reached. Try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <form
      className="mt-6 space-y-3 text-left"
      onSubmit={(event) => {
        event.preventDefault()
        void (sentFor ? reset() : send())
      }}
    >
      {sentFor === undefined ? (
        <>
          <label htmlFor={`${id}-name`} className="block text-ui font-medium">
            Username or email
          </label>
          <Input
            id={`${id}-name`}
            autoComplete="username"
            enterKeyHint="send"
            autoFocus
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={pending || !isSignInName(typed)}
          >
            {pending && <Loader2Icon className="animate-spin" />}
            Email me a code
          </Button>
        </>
      ) : (
        <>
          <p className="text-ui text-muted-foreground">
            If <span className="font-medium text-foreground">{sentFor}</span> is you in Beacon, a
            code is on its way to your email. It works for 5 minutes.
          </p>
          <label htmlFor={`${id}-code`} className="block text-ui font-medium">
            Code
          </label>
          <Input
            id={`${id}-code`}
            inputMode="numeric"
            enterKeyHint="go"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
              setRefused(undefined)
            }}
          />
          <label htmlFor={`${id}-password`} className="block text-ui font-medium">
            New password
          </label>
          <Input
            id={`${id}-password`}
            type="password"
            autoComplete="new-password"
            enterKeyHint="go"
            value={password}
            aria-invalid={refused !== undefined || undefined}
            onChange={(event) => {
              setPassword(event.target.value)
              setRefused(undefined)
            }}
          />
          <p className="text-xs text-muted-foreground">
            At least 12 characters. A few unrelated words make a strong one.
          </p>
          {refused && <p className="text-xs text-destructive">{refused}</p>}
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={pending || !otp || !password}
          >
            {pending && <Loader2Icon className="animate-spin" />}
            Set password and sign in
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            disabled={pending}
            onClick={() => void send()}
          >
            Send another code
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        disabled={pending}
        onClick={onBack}
      >
        <ArrowLeftIcon />
        Back to signing in with a password
      </Button>
    </form>
  )
}
